// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { addQuotes, createCSVContent } from '../../utils';
import { sortContainerSizes } from '../size/sortContainerSizes';
import { compareCropsByName } from './sortCropsByName';

interface Crop {
  readonly crop_name: string;
  readonly total_grow_time: number;
  readonly max_hold_time: number;
  readonly harvest_buffer_time: number;
  readonly sowing_density?: string | null;
  readonly sow_density_unit: string;
  readonly yield_per_tray: string;
  readonly yield_unit: string;
  readonly notes?: string | null;
  readonly buffer_percentage: number;
  readonly sizes: {
    readonly size: string;
    readonly unit: string;
    readonly price_per_item?: string | null;
  }[];
  readonly steps: {
    readonly name: string;
    readonly time_to_start_after_first_step: number;
    readonly description?: string | null;
  }[];
}
const secondToDays = (seconds: number) => seconds / (24 * 60 * 60);

// Edits to this function should be mirrored in parseCropUpload.ts
export const makeCropsCSV = (crops: Crop[], currencySymbol: string) => {
  const sorted = [...crops];
  sorted.sort((a, b) => compareCropsByName(a.crop_name, b.crop_name));

  const maxSizesCount = Math.max(...sorted.map((crop) => crop.sizes.length));
  const maxStepsCount = Math.max(...sorted.map((crop) => crop.steps.length));

  const data = sorted.map((crop) => {
    const preSortedSizes = sortContainerSizes(
      [...crop.sizes].map((size) => ({
        ...size,
        size: Number.parseFloat(size.size),
      })),
    );
    const sizes = [
      ...preSortedSizes.flatMap((size) => [
        `${size.size} ${size.unit}`,
        size.price_per_item ? `${currencySymbol}${size.price_per_item}` : '',
      ]),
    ];
    // Push for each size and price
    while (sizes.length < maxSizesCount * 2) {
      sizes.push('');
    }
    const preSortedSteps = [...crop.steps];
    preSortedSteps.sort((a, b) => a.time_to_start_after_first_step - b.time_to_start_after_first_step);
    const steps = [
      ...preSortedSteps.flatMap((step) => [
        step.name,
        secondToDays(step.time_to_start_after_first_step).toString(),
        step.description ?? '',
      ]),
    ];
    while (steps.length < maxStepsCount * 3) {
      steps.push('');
    }

    return [
      crop.crop_name,
      crop.yield_per_tray.toString(),
      crop.yield_unit,
      secondToDays(crop.total_grow_time).toString(),
      crop.sowing_density?.toString() ?? '',
      crop.sow_density_unit,
      crop.buffer_percentage.toString(),
      secondToDays(crop.max_hold_time).toString(),
      secondToDays(crop.harvest_buffer_time).toString(),
      crop.notes ?? '',
      ...sizes,
      ...steps,
    ].map(addQuotes);
  });
  const headers = [
    'Name',
    'Yield Per Tray',
    'Yield Per Tray Unit',
    'Total Grow Time',
    'Sowing Density Per Tray',
    'Sowing Density Unit',
    'Safety Margin',
    'Shelf Life Days',
    'Harvest Buffer Days',
    'Notes',
    ...Array.from({ length: maxSizesCount }, (_, i) => [`Size ${i + 1}`, `Price Per Item ${i + 1}`]).flat(),
    ...Array.from({ length: maxStepsCount }, (_, i) => [
      `Step ${i + 1} Name`,
      `Step ${i + 1} Start Day`,
      `Step ${i + 1} Description`,
    ]).flat(),
  ];

  return createCSVContent({ data: [headers, ...data] });
};
